import React, { useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  setPage,
  setTake,
  selectSearchCriteria,
  selectRama,
  selectCompania,
  selectEstado,
  clearData,
} from "./actions";
import Searcher from "~styled/searcher/searcher";
import { Typography, Button, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import Pagination from "~styled/pagination/Pagination";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import { paginationOptions } from "~constants/pagination";
import Selector, { type OptionKV } from "~styled/selector/Selector";
import { getRamas } from "../listadoPolizasMultirama/actions";
import { paths, altaPolizaMultiramaPath } from "~constants/referenceNames";
import { companiasSegurosOptions, companiasFilter } from "./companias";
import { useFeaturesEnabled } from "~hooks/useFeatureEnabled";
import {
  estadosPolizasOptions,
  estadosPolizasFilter,
} from "~constants/polizaEstados";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 20,
    width: "100%",
    minWidth: "10px",
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  filters: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingBottom: "20px",
  },
  label: {
    fontSize: "10px",
    fontWeight: 500,
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.3",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.grey2,
    paddingLeft: 5,
    paddingBottom: 5,
  },
  horizontalSpace: { paddingRight: "10px" },
  horizontalSpaceSearcher: { paddingRight: "10px", width: "100%" },
  searcherAndTitle: {
    display: "flex",
    flexDirection: "column",
    width: "220px",
  },
  dateSelectorAndTitle: {
    display: "flex",
    flexDirection: "column",
    width: "220px",
  },
  btnPoliza: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "200px",
    paddingLeft: "15px",
    marginTop: "12px",
  },
  ramaSelector: {
    width: "300px",
  },
}));

type V = string | number;

interface SliceMultirama {
  isLoading: boolean;
  ramasOptions: Record<string, string>;
  ramasFilter: OptionKV<V>[];
  totalRegistros: number;

  take: V;
  page: number;
  searchCriteria: string;
  rama: V;
  compania: V;
  estado: V;
}

const useReduxSelector = () =>
  useSelector<any, SliceMultirama>((state) => ({
    isLoading: state.listadoPolizaMultiramaReducer.isLoading,
    ramasOptions: state.listadoPolizaMultiramaReducer.ramasOptions,
    ramasFilter: state.listadoPolizaMultiramaReducer.ramasFilter,
    totalRegistros: state.listadoPolizaMultiramaReducer.totalRegistros,

    take: state.listadoPolizaMultiramaFiltroReducer.take,
    page: state.listadoPolizaMultiramaFiltroReducer.page,
    searchCriteria: state.listadoPolizaMultiramaFiltroReducer.searchCriteria,
    rama: state.listadoPolizaMultiramaFiltroReducer.rama,
    compania: state.listadoPolizaMultiramaFiltroReducer.compania,
    estado: state.listadoPolizaMultiramaFiltroReducer.estado,
  }));

const messageLoading = "Cargando su consulta previa" as const;

const Filtros: React.FC = () => {
  const {
    take,
    page,
    isLoading,
    totalRegistros,
    searchCriteria,
    rama,
    compania,
    ramasOptions,
    ramasFilter,
    estado,
  } = useReduxSelector();

  const dispatch = useDispatch();
  const classes = useStyles();

  useEffect(() => {
    dispatch(getRamas());
    return () => {
      dispatch(clearData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const { polizasMultiramaRegistrarIsEnabled } = useFeaturesEnabled() as {
    polizasMultiramaRegistrarIsEnabled: boolean;
  };

  const handleChange = <T,>(
    event: { target: { value: T } },
    action: (v: T) => any
  ) => {
    dispatch(setPage(0));
    dispatch(action(event.target.value));
  };

  const handleChangePage = (value: number) => {
    dispatch(setPage(value));
  };

  const estadoLabelMap = estadosPolizasOptions as Record<string, string>;
  const companiaLabelMap = companiasSegurosOptions as Record<string, string>;

  const paginationOptionsStr = React.useMemo(
    () =>
      paginationOptions.map((o) => ({
        label: String(o.label),
        value: o.value,
      })),
    []
  );

  return (
    <div className={classes.root}>
      <>
        <div className={classes.filtersContainer}>
          <div className={classes.filters}>
            <div className={classes.searcherAndTitle}>
              <Typography variant="h3" className={classes.label}>
                BUSCAR
              </Typography>
              <div className={classes.horizontalSpaceSearcher}>
                <Searcher
                  borderSquared
                  value={searchCriteria}
                  filter={searchCriteria}
                  placeholder="N° Póliza, DNI, Titular, Dominio"
                  onChange={(event) =>
                    handleChange(event, selectSearchCriteria)
                  }
                  onClear={() =>
                    handleChange(
                      { target: { value: "" } },
                      selectSearchCriteria
                    )
                  }
                />
              </div>
            </div>

            <div
              className={`${classes.searcherAndTitle} ${classes.ramaSelector}`}
            >
              <Typography variant="h3" className={classes.label}>
                RAMA
              </Typography>
              <div className={classes.horizontalSpace}>
                <Selector<V>
                  value={ramasOptions[String(rama)]}
                  options={ramasFilter as OptionKV<V>[]}
                  onChange={(value) =>
                    handleChange({ target: { value } }, selectRama)
                  }
                  onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className={classes.searcherAndTitle}>
              <Typography variant="h3" className={classes.label}>
                COMPAÑÍA
              </Typography>
              <div className={classes.horizontalSpace}>
                <Selector<V>
                  value={companiaLabelMap[String(compania)]}
                  options={companiasFilter as OptionKV<V>[]}
                  onChange={(value) =>
                    handleChange({ target: { value } }, selectCompania)
                  }
                  onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                  disabled={isLoading}
                />
              </div>
            </div>

            {polizasMultiramaRegistrarIsEnabled && (
              <div className={classes.btnPoliza}>
                <Tooltip title="Registrar Póliza">
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to={paths[altaPolizaMultiramaPath]}
                  >
                    REGISTRAR PÓLIZA
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </>

      <>
        <div className={classes.filtersContainer}>
          <div className={classes.filters}>
            <div className={classes.searcherAndTitle}>
              <Typography variant="h3" className={classes.label}>
                ESTADO
              </Typography>
              <div className={classes.horizontalSpace}>
                <Selector<V>
                  value={estadoLabelMap[String(estado)]}
                  options={estadosPolizasFilter as OptionKV<V>[]}
                  onChange={(value) =>
                    handleChange({ target: { value } }, selectEstado)
                  }
                  onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {!isLoading && (
            <Pagination<V>
              rowsPerPage={take}
              onChangeRowsPerPage={(value) =>
                handleChange({ target: { value } }, setTake)
              }
              page={page}
              onChangePage={handleChangePage}
              totalRegistros={totalRegistros}
              options={paginationOptionsStr as OptionKV<V>[]}
            />
          )}
        </div>
      </>
    </div>
  );
};

export default Filtros;
